import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { BenefitsFinancialImpactCalculationDto } from '@flexyoo/api';
import { CartManagementMode } from '@flexyoo/api/models';
import { FlexyooAlertsService, WhiteLabelService } from '@flexyoo/common';
import { FinancialImpactDataAccessService } from '@flexyoo/employee/data-access';
import { employeeSelectedBenefitsSelectors } from '@flexyoo/employee/store';
import { environment } from '@flexyoo/environment';
import { downloadURI } from '@flexyoo/shared/functions';
import { FlexyooLocalizedNamePipe, FlexyooNumberPipe } from '@flexyoo/shared/pipes';
import { LoaderComponent } from '@flexyoo/shared/ui';
import { tenantSettingsSelectors } from '@flexyoo/tenant/store';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, map, startWith, switchMap, tap } from 'rxjs/operators';
import { EmployeeCartSimulationComponent } from '../employee-cart-simulation/employee-cart-simulation.component';
import { buildCartSimulationView } from '../employee-cart-simulation/employee-cart-simulation.util';
import { buildFinancialImpactReportView } from './employee-financial-impact-report.util';

const PDF_FILE_NAME = 'financial-impact-report.pdf';

/**
 * The dialog opens on the narrow "Cart simulation" card and widens for the report.
 * The design draws the card 455px wide at a ~12px base font; the app renders it at
 * 16px, so the width is scaled by the same factor. The report keeps its width.
 */
export const NET_SIMULATION_DIALOG_WIDTH = {
  cart: '600px',
  report: '720px',
} as const;

/** "Cart simulation" card first, then the "Net simulation overview" report. */
type NetSimulationStep = keyof typeof NET_SIMULATION_DIALOG_WIDTH;

/** 50322: without the "Cart simulation" card the dialog opens straight on the report. */
const INITIAL_STEP: NetSimulationStep = environment.showCartSimulationStep ? 'cart' : 'report';

/** Width to open the dialog with, the one of its first step. */
export const NET_SIMULATION_DIALOG_INITIAL_WIDTH = NET_SIMULATION_DIALOG_WIDTH[INITIAL_STEP];

@Component({
  selector: 'flexyoo-employee-financial-impact-report',
  templateUrl: './employee-financial-impact-report.component.html',
  styleUrl: './employee-financial-impact-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    TranslateModule,
    MatIconModule,
    MatButtonModule,
    LoaderComponent,
    FlexyooNumberPipe,
    FlexyooLocalizedNamePipe,
    EmployeeCartSimulationComponent,
  ],
})
export class EmployeeFinancialImpactReportComponent {
  private readonly store = inject(Store);
  private readonly dataAccess = inject(FinancialImpactDataAccessService);
  private readonly dialogRef = inject(MatDialogRef<EmployeeFinancialImpactReportComponent>);
  private readonly translateService = inject(TranslateService);
  private readonly alerts = inject(FlexyooAlertsService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly wls = inject(WhiteLabelService);

  protected readonly isLoading = signal(false);
  protected readonly hasError = signal(false);
  protected readonly isDownloading = signal(false);
  protected readonly step = signal<NetSimulationStep>(INITIAL_STEP);
  /** 50322: the report offers a way back only when the "Cart simulation" card exists. */
  protected readonly showCartSimulationStep = environment.showCartSimulationStep;

  private readonly scrollContainer = viewChild.required<ElementRef<HTMLElement>>('scrollContainer');

  protected readonly currLang = toSignal(
    this.translateService.onLangChange.pipe(
      map((change) => change?.lang),
      startWith(this.translateService.currentLang),
      distinctUntilChanged()
    )
  );

  private readonly reportData = signal<BenefitsFinancialImpactCalculationDto | null>(null);

  private readonly cartBenefits = toSignal(
    this.store.select(employeeSelectedBenefitsSelectors.selectCartBenefits),
    { initialValue: [] }
  );

  private readonly currentCartBenefits = toSignal(
    this.store.select(employeeSelectedBenefitsSelectors.selectCurrentCartBenefits),
    { initialValue: [] }
  );

  private readonly isCartMode = toSignal(
    this.store
      .select(tenantSettingsSelectors.selectCartManagementMode)
      .pipe(map((mode) => mode === CartManagementMode.Cart)),
    { initialValue: false }
  );

  private readonly currentCart = toSignal(
    this.store.select(employeeSelectedBenefitsSelectors.selectCurrentCart)
  );

  /** In cart mode the choice period holds several carts, so only the open one is reported on. */
  private readonly currentCartId = computed(() =>
    this.isCartMode() ? (this.currentCart()?.id ?? null) : null
  );

  /**
   * A cart keeps its cancelled benefits (removed by the employee or rejected by HR),
   * but the backend leaves them out of the calculation, so they do not count here.
   */
  private readonly reportBenefits = computed(() =>
    (this.currentCartId() === null ? this.cartBenefits() : this.currentCartBenefits()).filter(
      (benefit) => benefit.status !== 'cancelled'
    )
  );

  private readonly basketBenefits = toSignal(
    this.store.select(employeeSelectedBenefitsSelectors.selectBenefitsResponse),
    { initialValue: [] }
  );

  /**
   * The report lists the benefits the backend calculated it for, which are not only
   * those of the cart (e.g. approved benefits still running), so their names are
   * looked up among all the employee's benefits.
   */
  private readonly allBenefits = toSignal(
    this.store.select(employeeSelectedBenefitsSelectors.selectAllBasketBenefits),
    { initialValue: [] }
  );

  /**
   * 50477: the disclaimer names the date the estimate is made for, 1 January of the
   * year after the calculation, written as on the PDF of the report.
   */
  protected readonly disclaimerDate = computed(() => {
    const calculationDate = this.reportData()?.CalculationDate;
    const calculationYear = (
      calculationDate ? new Date(calculationDate) : new Date()
    ).getFullYear();

    return `01/01/${calculationYear + 1}`;
  });

  protected readonly isCartEmpty = computed(() => this.reportBenefits().length === 0);

  protected readonly report = computed(() => {
    const impact = this.reportData();

    if (!impact) {
      return null;
    }

    return buildFinancialImpactReportView(impact, this.allBenefits(), this.basketBenefits());
  });

  private readonly ssBasket = toSignal(
    this.store.select(employeeSelectedBenefitsSelectors.selectSSBasket)
  );

  protected readonly cartSimulation = computed(() => {
    const impact = this.reportData();

    if (!impact) {
      return null;
    }

    return buildCartSimulationView(impact, this.reportBenefits(), this.ssBasket());
  });

  private readonly reload$ = new Subject<void>();

  constructor() {
    // The report always mirrors the cart (FR-5.4): every add, remove or change
    // of a selected benefit triggers a fresh calculation without closing the dialog.
    effect(() => {
      // reading the cart is what subscribes the report to it
      this.reportBenefits();

      untracked(() => this.reload$.next());
    });

    this.reload$
      .pipe(
        // a newer cart state cancels the requests still in flight, so two
        // responses can never land out of order
        switchMap(() => this.loadReport()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  protected onClose(): void {
    this.dialogRef.close();
  }

  protected onOpenReport(): void {
    this.goTo('report');
  }

  protected onBack(): void {
    this.goTo('cart');
  }

  protected onDownloadPdf(): void {
    if (this.isDownloading()) {
      return;
    }

    this.isDownloading.set(true);

    const cartId = this.currentCartId();

    this.dataAccess
      .downloadReportPdf(cartId === null ? {} : { EmployeeCartId: cartId })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isDownloading.set(false);

          // an empty body means the file never arrived, which is a failure the
          // user has to hear about just like a rejected request
          if (!response?.body) {
            this.showDownloadError();

            return;
          }

          const fileName =
            response.headers.get('x-return-file') ??
            response.headers.get('x-file-name') ??
            PDF_FILE_NAME;

          downloadURI(window.URL.createObjectURL(response.body), fileName, false);
        },
        error: () => {
          this.isDownloading.set(false);
          this.showDownloadError();
        },
      });
  }

  protected onRetry(): void {
    this.reload$.next();
  }

  /** Both steps share one scroll area, so each one starts from its top. */
  private goTo(step: NetSimulationStep): void {
    this.step.set(step);
    this.dialogRef.updateSize(NET_SIMULATION_DIALOG_WIDTH[step]);
    this.scrollContainer().nativeElement.scrollTop = 0;
  }

  /**
   * The global interceptor cannot help here: the endpoint answers with a blob, and
   * its error body is either not JSON or carries no `Errors` array, so the generic
   * alert never fires and the failure would pass unnoticed.
   */
  private showDownloadError(): void {
    this.alerts.addAlert({
      type: 'error',
      text: this.translateService.instant('employee.financial-impact-report.download-error'),
    });
  }

  private loadReport(): Observable<BenefitsFinancialImpactCalculationDto> {
    if (this.isCartEmpty()) {
      this.reportData.set(null);
      this.isLoading.set(false);
      this.hasError.set(false);

      return EMPTY;
    }

    this.isLoading.set(true);
    this.hasError.set(false);

    const cartId = this.currentCartId();

    const impact$ =
      cartId === null
        ? this.dataAccess.loadChoicePeriodBenefitsImpact()
        : this.dataAccess.loadCartBenefitsImpact(cartId);

    return impact$.pipe(
      tap((impact) => {
        this.reportData.set(impact);
        this.isLoading.set(false);
      }),
      catchError(() => {
        this.isLoading.set(false);
        this.hasError.set(true);

        return EMPTY;
      })
    );
  }
}
